import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useBuilding } from '../../../Helpers/BuildingContext';
import {
    getAllUniqueHazardsFromBuilding,
    getRiskWarningsFromBuilding,
    getWorkEnvironmentRisksFromBuilding,
    HazardData,
    RiskConstructionData,
    WorkEnvironmentRiskData,
} from '../../../Helpers/utils';

export type RisksViewMode = 'blueprint' | 'map' | 'list';

const VIEW_MODE_LABELS: Record<RisksViewMode, string> = {
    blueprint: 'Ritning',
    map: 'Karta',
    list: 'Lista',
};

const VIEW_MODE_PRIORITY: RisksViewMode[] = ['blueprint', 'map', 'list'];

export { VIEW_MODE_LABELS, VIEW_MODE_PRIORITY };

export const useRisksOverview = () => {
    const { currentBuilding, loadingBuilding, buildingError } = useBuilding();
    const { id: buildingKey } = useParams<{ id: string }>();

    const hazards = useMemo<HazardData[]>(() =>
        currentBuilding ? getAllUniqueHazardsFromBuilding(currentBuilding) : [],
        [currentBuilding]
    );

    const riskConstructions = useMemo<RiskConstructionData[]>(() =>
        currentBuilding ? getRiskWarningsFromBuilding(currentBuilding) : [],
        [currentBuilding]
    );

    const workEnvironmentRisks = useMemo<WorkEnvironmentRiskData[]>(() =>
        currentBuilding ? getWorkEnvironmentRisksFromBuilding(currentBuilding) : [],
        [currentBuilding]
    );

    // Collect all component keys referenced by hazards and risk constructions
    const riskComponentKeys = useMemo(() => {
        const keys = new Set<string>();
        hazards.forEach(h => h.components.forEach(c => keys.add(c.key)));
        riskConstructions.forEach(r => r.components.forEach(c => keys.add(c.key)));
        return keys;
    }, [hazards, riskConstructions]);

    const availableModes = useMemo<RisksViewMode[]>(() => {
        const components = currentBuilding?.model.structure?.components;

        // Check if any risk-bearing component has blueprint/map locations
        let hasBlueprint = false;
        let hasMap = false;

        if (components) {
            for (const key of riskComponentKeys) {
                const comp = components[key];
                if (!comp) continue;
                if (comp.blueprintLocations && Object.keys(comp.blueprintLocations).length > 0) {
                    hasBlueprint = true;
                }
                if (comp.mapLocations && Object.keys(comp.mapLocations).length > 0) {
                    hasMap = true;
                }
                if (hasBlueprint && hasMap) break;
            }
        }

        // Also check work environment risk rooms for blueprint locations
        if (!hasBlueprint) {
            hasBlueprint = workEnvironmentRisks.some(w =>
                w.blueprintLocations && Object.keys(w.blueprintLocations).length > 0
            );
        }

        // Map requires a map image key
        hasMap = hasMap && !!currentBuilding?.model.currentMapImageKey;

        const modes: RisksViewMode[] = [];
        if (hasBlueprint) modes.push('blueprint');
        if (hasMap) modes.push('map');
        modes.push('list');
        return modes;
    }, [riskComponentKeys, workEnvironmentRisks, currentBuilding]);

    const counts = useMemo(() => ({
        hazards: hazards.length,
        riskConstructions: riskConstructions.length,
        workEnvironmentRisks: workEnvironmentRisks.length,
        total: hazards.length + riskConstructions.length + workEnvironmentRisks.length,
    }), [hazards, riskConstructions, workEnvironmentRisks]);

    return {
        buildingKey, currentBuilding, loadingBuilding, buildingError,
        hazards, riskConstructions, workEnvironmentRisks,
        availableModes, counts,
    };
};
